import { useEffect, useRef, useState, type ReactNode } from 'react';
import { isRetryableAuthCheckError, useAuth } from '@discovr/common';

import { PageLoader } from './components/common/PageLoader';
import { Button } from './components/ui/button';

const RETRY_COPY = {
  unavailable: "Discovr didn't answer just now. You're still signed in. Try again in a moment.",
  network: "We couldn't reach Discovr. Check your connection, then try again. You're still signed in.",
} as const;

/**
 * Holds the router until the account check settles. A check that failed for a reason a retry can fix
 * (a 5xx, no response) keeps the session and shows Try again; only a real auth failure reaches the login form.
 */
export function AuthGate({ children }: { children: (isAuthenticated: boolean) => ReactNode }) {
  const { user, loading, authCheckError, retryAuthCheck, signOut } = useAuth();
  if (loading) return <PageLoader />;
  if (user || !isRetryableAuthCheckError(authCheckError)) return <>{children(!!user)}</>;
  return <RetryScreen network={authCheckError === 'network'} onRetry={retryAuthCheck} signOut={signOut} />;
}

function RetryScreen({ network, onRetry, signOut }: { network: boolean; onRetry: () => void; signOut: () => Promise<void> }) {
  const [signOutFailed, setSignOutFailed] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The screen replaces the loader or the app, so focus would otherwise be lost.
  useEffect(() => headingRef.current?.focus(), []);

  const handleSignOut = () => {
    setSignOutFailed(false);
    signOut().catch((err: unknown) => {
      console.warn('[AuthGate] sign out failed', err);
      setSignOutFailed(true);
    });
  };

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <h1 ref={headingRef} tabIndex={-1} className="text-xl font-medium text-foreground outline-none">We couldn&apos;t check your account</h1>
        <p className="text-muted-foreground">{network ? RETRY_COPY.network : RETRY_COPY.unavailable}</p>
        <div className="flex gap-3">
          <Button onClick={onRetry}>Try again</Button>
          <Button variant="outline" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
        {signOutFailed && (
          <p role="alert" className="text-destructive">
            Couldn&apos;t sign out. Try again.
          </p>
        )}
      </div>
    </main>
  );
}
